// ── ErrorState — the presentational error surface ────────────────────────────
// PRESENTATIONAL ONLY, primitive/structural props — no ApiError dependency at
// the type level: `instanceof` is brittle across separately bundled MFE
// copies, and primitive props keep this composable regardless of caller.
// ErrorState NEVER reports — TracedErrorBoundary (ASC-6297) performs the
// single report in componentDidCatch, so a rerender cannot double-report.
// Spec: UI Specs: errorzen v2.1 §ErrorState (FR-026, Epic ASC-6291).
//
// Thin wrapper around zendesign's StateDisplay (the repo's standard
// empty/error surface) — contributes only the Support ID copy row, always
// rendered below whatever action(s) StateDisplay shows. No opinion on
// sizing/centering/focus beyond StateDisplay's own: every real caller already
// wraps ErrorState in its own sized container (TracedErrorBoundary fallbacks
// all render their own centered `<main>`/`<div>`), so a `variant` prop here
// was pure duplication — removed. `className` passes straight through to
// StateDisplay, same as StateDisplay's own contract. Ref: ASC-6756.
//
// HOOK-FREE top-level body intentionally — the contract test (and any
// non-React caller) invokes `ErrorState(props)` as a plain function, where
// hooks would throw. Stateful behavior (clipboard status) lives in
// SupportIdRow, whose hooks only run when React renders the tree.
// Ref: ASC-6339

import type { ReactElement, ReactNode } from 'react';
import { Button } from '@oraczen/zendesign-ui/components/button';
import { StateDisplay } from '@oraczen/zendesign-ui/components/state-display';
import { CircleAlert, RefreshCw, type LucideProps } from 'lucide-react';
import { SupportIdRow } from './SupportIdRow';
export interface ErrorStateProps {
  /** Already localized by the caller (via `toErrorPresentation`). */
  title: string;
  /** Already localized/validated by the caller. */
  description: string;
  /** The request_id — the Support ID row is hidden when absent. */
  supportId?: string | null;
  /** Try-again is shown only when true AND `onRetry` is provided. */
  retryable?: boolean;
  /** Caller wires `resetErrorBoundary` or a query reset. */
  onRetry?: () => void;
  /** True while a retry is in flight — disables the button (no double submit). */
  retrying?: boolean;
  /** Error glyph — defaults to `CircleAlert` (this component's glyph before ASC-6756). */
  icon?: React.ComponentType<LucideProps>;
  /** Passthrough to StateDisplay; omit to inherit its own default (`true`). */
  isBordered?: boolean;
  /** Icon shown on the default retry button — defaults to `RefreshCw`, same as StateDisplay's own `retryIcon`. */
  retryIcon?: ReactNode;
  /** Label for the default retry button — defaults to `"Try again"`, same as StateDisplay's own `retryLabel`. */
  retryLabel?: string;
  /**
   * Replaces the default "Try again" button. The Support ID row is ALWAYS
   * appended below this, independent of whether it's overridden — the row is
   * what this wrapper contributes over a bare StateDisplay.
   */
  actions?: ReactNode;
  /** Passthrough to StateDisplay's own className — sizing/centering is the caller's job. */
  className?: string;
}
export function ErrorState({
  title,
  description,
  supportId = null,
  retryable = false,
  onRetry,
  retrying = false,
  icon = CircleAlert,
  isBordered,
  retryIcon = <RefreshCw className="size-3.5" />,
  retryLabel = 'Try again',
  actions,
  className
}: ErrorStateProps): ReactElement {
  const showRetry = retryable && typeof onRetry === 'function';
  function handleRetryClick(): void {
    // Double-submission guard — clicks while a retry is in flight are ignored.
    if (retrying) return;
    onRetry?.();
  }
  const primaryAction = actions ?? (showRetry ? <Button type="button" variant="default" size="tiny" isCurved onClick={handleRetryClick} disabled={retrying} loading={retrying}>
        {retryIcon}
        {retryLabel}
      </Button> : null);
  return <StateDisplay role="alert"
  // StateDisplay hardcodes role="status" aria-live="polite" BEFORE spreading
  // props, so role="alert" above wins — but aria-live does NOT unless we set
  // it too. role="alert" implies assertive; leaving StateDisplay's explicit
  // "polite" in place would downgrade the announcement, since an explicit
  // aria-live beats a role's implicit one. Ref: ASC-6756.
  aria-live="assertive" variant="error" icon={icon} title={title} description={description} isBordered={isBordered} className={className} actions={primaryAction || supportId ?
  // flex-col by default: on small screens the Support ID row sits
  // below the primary action. sm:flex-row puts them side by side
  // from the sm breakpoint up.
  //
  // sm:items-start is load-bearing: SupportIdRow is two lines tall
  // since ASC-9396 (button + the visible ID below it), so it defines
  // the row height. Under items-center the primary action centred
  // against that block and stopped lining up with the copy button
  // beside it. Ref: ASC-9396.
  <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start sm:gap-4">
            {primaryAction}
            {supportId ? <SupportIdRow supportId={supportId} /> : null}
          </div> : null} />;
}